import {
  OrderStats,
  OrderFilters,
  ApiOrderStatistics,
  PaymentModeFilters,
} from '../models/order.models';
import { ShimmerColumn } from '../../shared/components/loading-table-shimmer/loading-table-shimmer.component';
import { FilterCategory } from '../../shared/components/filter-modal/filter-modal.component';
import { PaymentMethodCode } from '../../shared/types/payment-method.enum';

/** Shared icon style for order stat cards. Reduces duplication in ADMIN_ORDER_STATS and ADMIN_DASHBOARD_EXTRA_ORDER_STATS. */
const ORDER_STAT_ICON_STYLE_BASE = {
  position: 'absolute',
  transform: 'rotate(-4deg)',
  'image-rendering': '-webkit-optimize-contrast',
  filter: 'none',
} as const;

function orderStatIconStyle(overrides: {
  right?: string;
  bottom?: string;
  width?: string;
  height?: string;
}): Record<string, string> {
  return { ...ORDER_STAT_ICON_STYLE_BASE, ...overrides } as Record<string, string>;
}

/** Reused icon position overrides to avoid repeating the same object in multiple stat cards. */
const ORDER_STAT_ICON_OVERRIDE_REVENUE = {
  right: '-40px',
  bottom: '-50px',
  width: '160px',
  height: '160px',
};
const ORDER_STAT_ICON_OVERRIDE_COMPLETED_PENDING = {
  right: '-20px',
  bottom: '-30px',
  width: '160px',
  height: '160px',
};

/**
 * Shared order statistics card definitions (admin & staff view).
 * Titles, colors, icons, and icon positioning are identical across
 * admin-orders and staff-orders components.
 */
export const ADMIN_ORDER_STATS: OrderStats[] = [
  {
    title: "Today's Revenue",
    value: '-',
    color: 'green',
    icon: 'assets/images/orders/todays-revenue-icon.svg',
    iconClass: 'todays-revenue-icon',
    iconStyle: orderStatIconStyle({
      right: '-40px',
      bottom: '-50px',
      width: '160px',
      height: '160px',
    }),
  },
  {
    title: "Today's Orders",
    value: '-',
    color: 'blue',
    icon: 'assets/images/orders/todays-orders-icon.svg',
    iconClass: 'todays-orders-icon',
    iconStyle: orderStatIconStyle({
      right: '-20px',
      bottom: '-40px',
      width: '140px',
      height: '140px',
    }),
  },
  {
    title: "Today's Single Washes",
    value: '-',
    color: 'purple',
    icon: 'assets/images/orders/completed-orders-icon.svg',
    iconClass: 'completed-orders-icon',
    iconStyle: orderStatIconStyle({
      right: '-20px',
      bottom: '-30px',
      width: '160px',
      height: '160px',
    }),
  },
  {
    title: "Today's Monthly Washes",
    value: '-',
    color: 'orange',
    icon: 'assets/images/orders/pending-orders-icon.svg',
    iconClass: 'pending-orders-icon',
    iconStyle: orderStatIconStyle({
      right: '-20px',
      bottom: '-30px',
      width: '160px',
      height: '160px',
    }),
  },
];

/**
 * Extra order stat cards shown only on admin dashboard (card collection, cash, prepaid, total revenue).
 * Use with ADMIN_ORDER_STATS: orderStats = [...ADMIN_ORDER_STATS.map(s=>({...s})), ...ADMIN_DASHBOARD_EXTRA_ORDER_STATS.map(s=>({...s}))].
 */
export const ADMIN_DASHBOARD_EXTRA_ORDER_STATS: OrderStats[] = [
  {
    title: "Today's Card Collection",
    value: '-',
    color: 'green',
    icon: 'assets/images/orders/todays-revenue-icon.svg',
    iconClass: 'todays-revenue-icon',
    iconStyle: orderStatIconStyle(ORDER_STAT_ICON_OVERRIDE_REVENUE),
  },
  {
    title: "Today's Cash Collection",
    value: '-',
    color: 'green',
    icon: 'assets/images/orders/todays-revenue-icon.svg',
    iconClass: 'todays-revenue-icon',
    iconStyle: orderStatIconStyle(ORDER_STAT_ICON_OVERRIDE_REVENUE),
  },
  {
    title: 'Prepaid Orders',
    value: '-',
    color: 'purple',
    icon: 'assets/images/orders/completed-orders-icon.svg',
    iconClass: 'completed-orders-icon',
    iconStyle: orderStatIconStyle(ORDER_STAT_ICON_OVERRIDE_COMPLETED_PENDING),
  },
  {
    title: 'Total Revenue',
    value: '-',
    color: 'green',
    icon: 'assets/images/orders/todays-revenue-icon.svg',
    iconClass: 'todays-revenue-icon',
    iconStyle: orderStatIconStyle(ORDER_STAT_ICON_OVERRIDE_REVENUE),
  },
];

/**
 * Map API order statistics to the 4 standard order stat cards (revenue, today's orders, single_washes, monthly_memberships).
 * Shared by OrdersService and admin-dashboard; dashboard may append extra cards.
 */
export function mapApiStatisticsToOrderStats(apiStats: ApiOrderStatistics): OrderStats[] {
  const singleWashes = apiStats.single_washes ?? 0;
  const monthlyMemberships = apiStats.monthly_memberships ?? 0;

  const formatValue = (val: number | undefined | null, isCurrency = false) => {
    if (val === 0 || val === null || val === undefined) return '-';
    if (isCurrency) return `$${val.toFixed(2)}`;
    return val.toString().padStart(2, '0');
  };

  const values = [
    formatValue(apiStats.todays_revenue, true),
    formatValue(apiStats.todays_orders),
    formatValue(singleWashes),
    formatValue(monthlyMemberships),
  ];
  return ADMIN_ORDER_STATS.map((card, i) => ({ ...card, value: values[i] }));
}

/**
 * Period-scoped stat cards for the super-admin dashboard (CWS-891).
 * Every card follows the dashboard date filter, so no title carries a "Today's" prefix — the
 * period band above the grid names the window once. Lifetime totals are deliberately absent:
 * they render on that band, not as cards, so the two scopes can never look alike.
 * Icons, colors and icon positioning match ADMIN_ORDER_STATS so the cards look unchanged.
 */
export const ADMIN_DASHBOARD_PERIOD_STATS: OrderStats[] = [
  {
    title: 'Revenue',
    value: '-',
    color: 'green',
    icon: 'assets/images/orders/todays-revenue-icon.svg',
    iconClass: 'todays-revenue-icon',
    iconStyle: orderStatIconStyle(ORDER_STAT_ICON_OVERRIDE_REVENUE),
  },
  {
    title: 'Orders',
    value: '-',
    color: 'blue',
    icon: 'assets/images/orders/todays-orders-icon.svg',
    iconClass: 'todays-orders-icon',
    iconStyle: orderStatIconStyle({
      right: '-20px',
      bottom: '-40px',
      width: '140px',
      height: '140px',
    }),
  },
  {
    title: 'Single Washes',
    value: '-',
    color: 'purple',
    icon: 'assets/images/orders/completed-orders-icon.svg',
    iconClass: 'completed-orders-icon',
    iconStyle: orderStatIconStyle(ORDER_STAT_ICON_OVERRIDE_COMPLETED_PENDING),
  },
  {
    title: 'Membership Washes',
    value: '-',
    color: 'orange',
    icon: 'assets/images/orders/pending-orders-icon.svg',
    iconClass: 'pending-orders-icon',
    iconStyle: orderStatIconStyle(ORDER_STAT_ICON_OVERRIDE_COMPLETED_PENDING),
  },
  {
    title: 'Card Collection',
    value: '-',
    color: 'green',
    icon: 'assets/images/orders/todays-revenue-icon.svg',
    iconClass: 'todays-revenue-icon',
    iconStyle: orderStatIconStyle(ORDER_STAT_ICON_OVERRIDE_REVENUE),
  },
  {
    title: 'Cash Collection',
    value: '-',
    color: 'green',
    icon: 'assets/images/orders/todays-revenue-icon.svg',
    iconClass: 'todays-revenue-icon',
    iconStyle: orderStatIconStyle(ORDER_STAT_ICON_OVERRIDE_REVENUE),
  },
  {
    title: 'Prepaid Orders',
    value: '-',
    color: 'purple',
    icon: 'assets/images/orders/completed-orders-icon.svg',
    iconClass: 'completed-orders-icon',
    iconStyle: orderStatIconStyle(ORDER_STAT_ICON_OVERRIDE_COMPLETED_PENDING),
  },
];

/**
 * Format a stat card value the way the order stat cards have always formatted them:
 * 0/null/undefined renders as '-', currency as `$x.xx`, plain counts zero-padded to 2 digits.
 * Kept separate from the identical inline helper in mapApiStatisticsToOrderStats, which is
 * shared with the shop-owner orders and cash reconciliation pages and must not change.
 */
function formatOrderStatValue(val: number | undefined | null, isCurrency = false): string {
  if (val === 0 || val === null || val === undefined) return '-';
  if (isCurrency) return `$${val.toFixed(2)}`;
  return val.toString().padStart(2, '0');
}

/**
 * Map window-scoped API order statistics onto the 7 super-admin dashboard period cards.
 * Reads only date-window fields, so every card reflects the selected period; lifetime figures
 * (lifetime_orders / lifetime_revenue) are shown on the period band instead.
 */
export function mapAdminDashboardPeriodStats(apiStats: ApiOrderStatistics): OrderStats[] {
  const values = [
    formatOrderStatValue(apiStats?.total_revenue, true),
    formatOrderStatValue(apiStats?.total_orders),
    formatOrderStatValue(apiStats?.single_washes),
    formatOrderStatValue(apiStats?.membership_washes),
    formatOrderStatValue(apiStats?.card_collection, true),
    formatOrderStatValue(apiStats?.cash_collection, true),
    formatOrderStatValue(apiStats?.prepaid_order_count),
  ];
  return ADMIN_DASHBOARD_PERIOD_STATS.map((card, i) => ({ ...card, value: values[i] }));
}

/**
 * Shimmer loading columns — identical across all orders components.
 */
export const ORDER_SHIMMER_COLUMNS: ShimmerColumn[] = [
  // CWS-1781: mirrors the 15-column order of the Orders list header.
  { label: 'Last visit', width: 'md' },
  { label: 'Order ID', width: 'sm' },
  { label: 'License plate', width: 'sm' },
  { label: 'Lane', width: 'sm' },
  { label: 'Status', width: 'sm' },
  { label: 'Wash type', width: 'sm' },
  { label: 'Package name', width: 'md' },
  { label: 'Order amount', width: 'sm' },
  { label: 'Customer name', width: 'md' },
  { label: 'Payment mode', width: 'sm' },
  { label: 'Payment status', width: 'sm' },
  { label: 'Employee name', width: 'md' },
  { label: 'Created', width: 'md' },
  { label: 'Discount', width: 'sm' },
  { label: 'Source', width: 'sm' },
  { label: 'Actions', width: 'xs' },
];

/**
 * Date range filter category for admin dashboard orders (today, yesterday, week, month, year, custom).
 * Combine with ADMIN_ORDER_FILTER_CATEGORIES to build full filterCategories and avoid duplicating carWashType/status/source.
 */
export const ADMIN_DASHBOARD_DATE_RANGE_FILTER_CATEGORY: FilterCategory = {
  id: 'dateRange',
  name: 'Date Range',
  type: 'radio',
  options: [
    { id: 'today', label: 'Today', value: 'today', selected: false },
    { id: 'yesterday', label: 'Yesterday', value: 'yesterday', selected: false },
    { id: 'week', label: 'This Week (Sun-Sat)', value: 'week', selected: false },
    { id: 'month', label: 'This Month', value: 'month', selected: false },
    { id: 'year', label: 'This Year', value: 'year', selected: false },
    { id: 'custom', label: 'Custom', value: 'custom', selected: false },
  ],
};

export const CAR_WASH_TYPE_FILTER_OPTIONS = [
  {
    id: 'monthly-membership',
    label: 'Monthly Membership',
    value: 'monthlyMembership',
    selected: false,
  },
  { id: 'single-wash', label: 'Single Wash', value: 'singleWash', selected: false },
  { id: 'prepaid-wash', label: 'Prepaid Wash', value: 'prepaidWash', selected: false },
  { id: 'rewash', label: 'Rewash', value: 'rewash', selected: false },
];

const ORDER_STATUS_FILTER_OPTIONS = [
  // `pending` is a real order_status (awaiting payment) and StatusFilters already carries it;
  // the popup simply never offered it (CWS-737).
  { id: 'pending', label: 'Pending', value: 'pending', selected: false },
  { id: 'completed', label: 'Completed', value: 'completed', selected: false },
  { id: 'scheduled', label: 'Scheduled', value: 'scheduled', selected: false },
  { id: 'canceled', label: 'Canceled', value: 'canceled', selected: false },
  { id: 'in-progress', label: 'In Progress', value: 'inProgress', selected: false },
];

/**
 * Built-in checkout Payment Modes always shown in the Orders filter.
 * Additional modes are loaded from GET `user/payment-modes`.
 * Option `value` keys match {@link PaymentModeFilters} (except dynamic options, which use API names).
 */
export const PAYMENT_MODE_FILTER_OPTIONS = [
  { id: 'tap-to-pay', label: 'Tap to Pay', value: 'tapToPay', selected: false },
  { id: 'cash', label: 'Cash', value: 'cash', selected: false },
  { id: 'card', label: 'Card Payment', value: 'card', selected: false },
  { id: 'link', label: 'Payment Link', value: 'link', selected: false },
];

/** Shown in Payment Mode filter only when the selected location has linked terminal readers. */
export const TERMINAL_PAYMENT_MODE_FILTER_OPTION = {
  id: 'terminal',
  label: 'Terminal Payment',
  value: 'terminal',
  selected: false,
};

export interface PaymentModeFilterOptionsConfig {
  /** When true, inserts Terminal Payment after Tap to Pay (matches checkout visibility). */
  showTerminalPayment?: boolean;
}

export const PAYMENT_MODE_FILTER_CATEGORY: FilterCategory = {
  id: 'paymentMode',
  name: 'Payment Mode',
  type: 'checkbox',
  options: PAYMENT_MODE_FILTER_OPTIONS,
};

/** Package filter category — options are loaded dynamically from PackageService. */
export const PACKAGE_FILTER_CATEGORY: FilterCategory = {
  id: 'package',
  name: 'Package',
  type: 'checkbox',
  options: [],
};

/** Discount Applied filter tokens (sent as `discount_applied` query param). */
export const DISCOUNT_APPLIED_FILTER_ALL = 'all';
export const DISCOUNT_APPLIED_FILTER_NONE = 'none';
export const DISCOUNT_APPLIED_FILTER_ANY = 'any';

/** Static Discount Applied options (dynamic discount rows are appended at runtime). */
export const DISCOUNT_APPLIED_STATIC_FILTER_OPTIONS: FilterCategory['options'] = [
  {
    id: 'discount-applied-all',
    label: 'All',
    value: DISCOUNT_APPLIED_FILTER_ALL,
    selected: true,
  },
  {
    id: 'discount-applied-none',
    label: 'No Discount',
    value: DISCOUNT_APPLIED_FILTER_NONE,
    selected: false,
  },
  {
    id: 'discount-applied-any',
    label: 'Any Discount',
    value: DISCOUNT_APPLIED_FILTER_ANY,
    selected: false,
  },
];

/** Discount Applied filter category — discount name options loaded from DiscountService. */
export const DISCOUNT_APPLIED_FILTER_CATEGORY: FilterCategory = {
  id: 'discountApplied',
  name: 'Discount Applied',
  type: 'dropdown',
  options: DISCOUNT_APPLIED_STATIC_FILTER_OPTIONS.map(option => ({ ...option })),
};

/** Normalize a payment-mode label/code for built-in duplicate detection. */
export function normalizePaymentModeFilterKey(value: string): string {
  return value.toLowerCase().replace(/[\s_-]/g, '');
}

const BUILT_IN_PAYMENT_MODE_FILTER_KEYS = new Set([
  'cash',
  'card',
  'link',
  'terminal',
  'taptopay',
  'cardpresent',
  'cardpayment',
  'paymentlink',
  'terminalpayment',
]);

export function isBuiltInPaymentModeFilterName(name: string): boolean {
  return BUILT_IN_PAYMENT_MODE_FILTER_KEYS.has(normalizePaymentModeFilterKey(name));
}

export interface PaymentModeApiFilterItem {
  id?: string | number;
  name?: string;
  is_active?: boolean;
}

function mapStaticPaymentModeFilterOption(
  option: (typeof PAYMENT_MODE_FILTER_OPTIONS)[number],
  selected?: PaymentModeFilters
): FilterCategory['options'][number] {
  return {
    ...option,
    selected:
      option.value === 'cash'
        ? !!selected?.cash
        : option.value === 'card'
          ? !!selected?.card
          : option.value === 'link'
            ? !!selected?.link
            : option.value === 'tapToPay'
              ? !!selected?.tapToPay
              : false,
  };
}

/** Built-in payment mode options, optionally including Terminal when location has readers. */
export function resolveStaticPaymentModeFilterOptions(
  selected?: PaymentModeFilters,
  config: PaymentModeFilterOptionsConfig = {}
): FilterCategory['options'] {
  const staticOptions = PAYMENT_MODE_FILTER_OPTIONS.map(option =>
    mapStaticPaymentModeFilterOption(option, selected)
  );

  if (!config.showTerminalPayment) {
    return staticOptions;
  }

  const tapToPayIndex = staticOptions.findIndex(option => option.value === 'tapToPay');
  const insertAt = tapToPayIndex >= 0 ? tapToPayIndex + 1 : staticOptions.length;
  const terminalOption = {
    ...TERMINAL_PAYMENT_MODE_FILTER_OPTION,
    selected: !!selected?.terminal,
  };

  return [...staticOptions.slice(0, insertAt), terminalOption, ...staticOptions.slice(insertAt)];
}

/**
 * Built-in Payment Mode options + active API modes (excluding built-in duplicates).
 */
export function buildPaymentModeFilterOptions(
  apiModes: PaymentModeApiFilterItem[] = [],
  selected?: PaymentModeFilters,
  config: PaymentModeFilterOptionsConfig = {}
): FilterCategory['options'] {
  const selectedAdditional = new Set(selected?.additional ?? []);
  const staticOptions = resolveStaticPaymentModeFilterOptions(selected, config);

  const seen = new Set<string>();
  const dynamicOptions: FilterCategory['options'] = [];
  for (const mode of apiModes) {
    if (mode?.is_active === false) {
      continue;
    }
    const name = String(mode?.name ?? '').trim();
    if (!name || isBuiltInPaymentModeFilterName(name)) {
      continue;
    }
    const key = normalizePaymentModeFilterKey(name);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    dynamicOptions.push({
      id: `payment-mode-${mode.id ?? name}`,
      label: name,
      value: name,
      selected: selectedAdditional.has(name),
    });
  }

  return [...staticOptions, ...dynamicOptions];
}

/** Rebuild Payment Mode category options from API modes + current selection. */
export function syncPaymentModeFilterCategoryOptions(
  categories: FilterCategory[],
  apiModes: PaymentModeApiFilterItem[],
  selected?: PaymentModeFilters,
  config: PaymentModeFilterOptionsConfig = {}
): FilterCategory[] {
  return categories.map(category => {
    if (category.id !== 'paymentMode') {
      return category;
    }
    return {
      ...category,
      options: buildPaymentModeFilterOptions(apiModes, selected, config),
    };
  });
}

/**
 * CWS-1238: a prepaid wash book has two order shapes — the `prepaid_wash_book` purchase and
 * one `prepaid_wash_redemption` per wash drawn from it. The Prepaid filter matched only the
 * purchase, so every redemption was hidden; a range whose prepaid activity was all
 * redemptions (the common case, since a book is bought once and redeemed many times) showed
 * an empty table. Exported so the filter mappings cannot drift apart again.
 *
 * CWS-1882 ("one sale = one order"): redeeming a prepaid wash no longer creates an order —
 * the visit is recorded against the purchase order instead. `prepaid_wash_redemption` stays
 * in this list because every redemption made before that change is still an order row, and
 * dropping it would hide that history from the Prepaid filter.
 */
export const PREPAID_FILTER_WASH_TYPES: readonly string[] = Object.freeze([
  'prepaid_wash_book',
  'prepaid_wash_redemption',
]);

/** Map UI Car Wash Type keys to API wash_type strings used on packages. */
export function mapCarWashTypeSelectionToApiWashTypes(carWashType: {
  monthlyMembership: boolean;
  singleWash: boolean;
  prepaidWash: boolean;
  rewash: boolean;
}): string[] {
  const types: string[] = [];
  if (carWashType.singleWash) types.push('single_wash');
  if (carWashType.monthlyMembership) types.push('monthly_membership');
  if (carWashType.prepaidWash) types.push(...PREPAID_FILTER_WASH_TYPES);
  if (carWashType.rewash) types.push('rewash');
  return types;
}

/**
 * Build Package filter options from packages.
 * When `apiWashTypes` is empty, all packages are shown; otherwise only packages whose
 * `wash_types` intersect the selected Car Wash Type API values.
 */
export function buildPackageFilterOptions(
  packages: { id: string; package_name: string; wash_types?: string[] }[],
  apiWashTypes: string[],
  selectedPackageIds: string[] = []
): FilterCategory['options'] {
  const selectedSet = new Set(selectedPackageIds);
  const filtered =
    apiWashTypes.length === 0
      ? packages
      : packages.filter(pkg =>
          (pkg.wash_types ?? []).some(washType => apiWashTypes.includes(washType))
        );

  return filtered.map(pkg => ({
    id: pkg.id,
    label: pkg.package_name || pkg.id,
    value: pkg.id,
    selected: selectedSet.has(pkg.id),
  }));
}

/**
 * Rebuild Package filter options from packages + current Car Wash Type checkbox state.
 * Preserves package selections that are still visible after the wash-type cascade.
 * Omits the Package category entirely when nothing matches (e.g. Rewash-only — packages
 * are not mapped to rewash).
 */
export function syncPackageFilterCategoryOptions(
  categories: FilterCategory[],
  packages: { id: string; package_name: string; wash_types?: string[] }[],
  selectedPackageIds?: string[]
): FilterCategory[] {
  const carWashCategory = categories.find(category => category.id === 'carWashType');
  const packageCategory = categories.find(category => category.id === 'package');
  const apiWashTypes = mapCarWashTypeSelectionToApiWashTypes({
    monthlyMembership:
      carWashCategory?.options.find(option => option.value === 'monthlyMembership')?.selected ??
      false,
    singleWash:
      carWashCategory?.options.find(option => option.value === 'singleWash')?.selected ?? false,
    prepaidWash:
      carWashCategory?.options.find(option => option.value === 'prepaidWash')?.selected ?? false,
    rewash: carWashCategory?.options.find(option => option.value === 'rewash')?.selected ?? false,
  });

  const preserveIds =
    selectedPackageIds ??
    packageCategory?.options.filter(option => option.selected).map(option => option.value) ??
    [];

  const packageOptions = buildPackageFilterOptions(packages, apiWashTypes, preserveIds);
  const withoutPackage = categories.filter(category => category.id !== 'package');

  // No matching packages (e.g. Rewash only) — hide the Package heading/section.
  if (packageOptions.length === 0) {
    return withoutPackage;
  }

  const nextPackageCategory: FilterCategory = {
    ...(packageCategory ?? PACKAGE_FILTER_CATEGORY),
    id: 'package',
    name: 'Package',
    type: 'checkbox',
    options: packageOptions,
  };

  const carWashIdx = withoutPackage.findIndex(category => category.id === 'carWashType');
  if (carWashIdx === -1) {
    return [...withoutPackage, nextPackageCategory];
  }

  return [
    ...withoutPackage.slice(0, carWashIdx + 1),
    nextPackageCategory,
    ...withoutPackage.slice(carWashIdx + 1),
  ];
}

/**
 * Resolve the filter option value for a discount row.
 * Prefer `logical_discount_id` so versioned edits still match historical orders.
 */
export function resolveDiscountAppliedFilterValue(discount: {
  id: string;
  logical_discount_id?: string | null;
}): string {
  const logical = String(discount.logical_discount_id ?? '').trim();
  if (logical) {
    return logical;
  }
  return String(discount.id ?? '').trim();
}

/**
 * Build Discount Applied dropdown options: All / No Discount / Any Discount + created discounts.
 * Includes both Summary (`normal`) and Custom Discount (`custom`) definitions.
 */
export function buildDiscountAppliedFilterOptions(
  discounts: { id: string; name?: string | null; logical_discount_id?: string | null }[],
  selectedValue: string = DISCOUNT_APPLIED_FILTER_ALL
): FilterCategory['options'] {
  const selected =
    selectedValue && String(selectedValue).trim()
      ? String(selectedValue).trim()
      : DISCOUNT_APPLIED_FILTER_ALL;

  const staticOptions = DISCOUNT_APPLIED_STATIC_FILTER_OPTIONS.map(option => ({
    ...option,
    selected: option.value === selected,
  }));

  const seen = new Set<string>();
  const discountOptions: FilterCategory['options'] = [];
  const sorted = [...discounts].sort((a, b) =>
    String(a.name ?? '').localeCompare(String(b.name ?? ''), undefined, { sensitivity: 'base' })
  );

  for (const discount of sorted) {
    const value = resolveDiscountAppliedFilterValue(discount);
    if (!value || seen.has(value)) {
      continue;
    }
    seen.add(value);
    const label = String(discount.name ?? '').trim() || value;
    discountOptions.push({
      id: `discount-applied-${value}`,
      label,
      value,
      selected: value === selected,
    });
  }

  // If a previously selected discount was deleted, keep a selected sentinel so UI state is honest.
  if (
    selected !== DISCOUNT_APPLIED_FILTER_ALL &&
    selected !== DISCOUNT_APPLIED_FILTER_NONE &&
    selected !== DISCOUNT_APPLIED_FILTER_ANY &&
    !seen.has(selected)
  ) {
    discountOptions.push({
      id: `discount-applied-${selected}`,
      label: selected,
      value: selected,
      selected: true,
    });
  }

  return [...staticOptions, ...discountOptions];
}

/** Rebuild Discount Applied category options from cached discounts + current selection. */
export function syncDiscountAppliedFilterCategoryOptions(
  categories: FilterCategory[],
  discounts: { id: string; name?: string | null; logical_discount_id?: string | null }[],
  selectedValue?: string
): FilterCategory[] {
  const discountCategory = categories.find(category => category.id === 'discountApplied');
  const selected =
    selectedValue ??
    discountCategory?.options.find(option => option.selected)?.value ??
    DISCOUNT_APPLIED_FILTER_ALL;

  const nextOptions = buildDiscountAppliedFilterOptions(discounts, selected);
  const nextCategory: FilterCategory = {
    ...(discountCategory ?? DISCOUNT_APPLIED_FILTER_CATEGORY),
    id: 'discountApplied',
    name: 'Discount Applied',
    type: 'dropdown',
    options: nextOptions,
  };

  const withoutDiscount = categories.filter(category => category.id !== 'discountApplied');
  // Prefer after Order Source (admin). Otherwise before Payment Mode (staff has no source).
  const sourceIdx = withoutDiscount.findIndex(category => category.id === 'source');
  if (sourceIdx !== -1) {
    return [
      ...withoutDiscount.slice(0, sourceIdx + 1),
      nextCategory,
      ...withoutDiscount.slice(sourceIdx + 1),
    ];
  }

  const paymentModeIdx = withoutDiscount.findIndex(category => category.id === 'paymentMode');
  if (paymentModeIdx === -1) {
    return [...withoutDiscount, nextCategory];
  }

  return [
    ...withoutDiscount.slice(0, paymentModeIdx),
    nextCategory,
    ...withoutDiscount.slice(paymentModeIdx),
  ];
}

/** Fresh PaymentModeFilters with every mode unselected. */
export function createDefaultPaymentModeFilters(): PaymentModeFilters {
  return {
    cash: false,
    card: false,
    link: false,
    tapToPay: false,
    terminal: false,
    additional: [],
  };
}

/**
 * Maps selected PaymentModeFilters keys to API `payment_method` values.
 * Card includes `card_present` so in-person card collections match the table "Card" label.
 */
export function mapPaymentModeFiltersToApiValues(filters: PaymentModeFilters): string[] {
  const modes: string[] = [];
  if (filters.cash) modes.push(PaymentMethodCode.Cash);
  if (filters.card) {
    modes.push(PaymentMethodCode.Card);
    modes.push(PaymentMethodCode.CardPresent);
  }
  if (filters.link) modes.push(PaymentMethodCode.Link);
  if (filters.tapToPay) modes.push(PaymentMethodCode.TapToPay);
  if (filters.terminal) modes.push(PaymentMethodCode.Terminal);
  if (Array.isArray(filters.additional)) {
    for (const mode of filters.additional) {
      const trimmed = String(mode ?? '').trim();
      if (trimmed) {
        modes.push(trimmed);
      }
    }
  }
  return modes;
}

/**
 * Filter categories for admin/main orders view (includes source + payment mode + package).
 */
export const ADMIN_ORDER_FILTER_CATEGORIES: FilterCategory[] = [
  {
    id: 'carWashType',
    name: 'Car Wash Type',
    options: CAR_WASH_TYPE_FILTER_OPTIONS,
  },
  {
    ...PACKAGE_FILTER_CATEGORY,
    options: [],
  },
  {
    id: 'status',
    name: 'Status',
    options: ORDER_STATUS_FILTER_OPTIONS,
  },
  {
    id: 'source',
    name: 'Order Source',
    options: [
      { id: 'way-com', label: 'Way.com', value: 'wayCom', selected: false },
      { id: 'way-app', label: 'Way App', value: 'wayApp', selected: false },
      { id: 'website', label: 'Website', value: 'website', selected: false },
    ],
  },
  {
    ...DISCOUNT_APPLIED_FILTER_CATEGORY,
    options: DISCOUNT_APPLIED_STATIC_FILTER_OPTIONS.map(option => ({ ...option })),
  },
  {
    ...PAYMENT_MODE_FILTER_CATEGORY,
    options: PAYMENT_MODE_FILTER_OPTIONS.map(option => ({ ...option })),
  },
];

/**
 * Filter categories for staff orders view (no source filter, uses period dropdown instead).
 * Discount Applied sits before Payment Mode (same relative order as admin after source).
 */
export const STAFF_ORDER_FILTER_CATEGORIES: FilterCategory[] = [
  {
    id: 'carWashType',
    name: 'Car Wash Type',
    type: 'checkbox',
    options: CAR_WASH_TYPE_FILTER_OPTIONS,
  },
  {
    ...PACKAGE_FILTER_CATEGORY,
    options: [],
  },
  {
    id: 'status',
    name: 'Status',
    type: 'checkbox',
    options: ORDER_STATUS_FILTER_OPTIONS,
  },
  {
    ...DISCOUNT_APPLIED_FILTER_CATEGORY,
    options: DISCOUNT_APPLIED_STATIC_FILTER_OPTIONS.map(option => ({ ...option })),
  },
  {
    ...PAYMENT_MODE_FILTER_CATEGORY,
    options: PAYMENT_MODE_FILTER_OPTIONS.map(option => ({ ...option })),
  },
];

/** Shared carWashType + status defaults used by all order filter factories below. */
function createBaseOrderFilters() {
  return {
    carWashType: {
      monthlyMembership: false,
      singleWash: false,
      prepaidWash: false,
      rewash: false,
    },
    status: {
      completed: false,
      pending: false,
      canceled: false,
      scheduled: false,
      inProgress: false,
    },
    paymentMode: createDefaultPaymentModeFilters(),
    packageIds: [] as string[],
    discountApplied: DISCOUNT_APPLIED_FILTER_ALL,
  };
}

/**
 * Default OrderFilters state for admin / main orders view.
 * Returns a fresh object each call to prevent shared-reference mutation.
 */
export function createDefaultAdminOrderFilters(): OrderFilters {
  return {
    ...createBaseOrderFilters(),
    source: {
      wayCom: false,
      scanAndGo: false,
      wayApp: false,
      website: false,
    },
  };
}

/**
 * Default OrderFilters state for staff orders view.
 */
export function createDefaultStaffOrderFilters(): OrderFilters {
  return {
    ...createBaseOrderFilters(),
    period: '',
  };
}

/**
 * How long a tunnel board row stays after the Tunnel Controller confirmed the wash
 * (`tunnel_completed_at`), CWS-1917 contract §4. A code constant shared with the
 * server, which returns rows completed less than this long ago — not an env value.
 */
export const TUNNEL_COMPLETED_LINGER_MS = 30000;

/** A `pending` wash with no device result after this long is shown as unconfirmed (contract §3). */
export const TUNNEL_WASH_NO_CONFIRMATION_MS = 15000;
